import { Platform } from 'react-native';
import { supabase } from '../../shared/supabase/supabaseClient';
import {
  invalidateStorefrontStatus,
  storefrontProfileCache,
} from './storefrontStatusCache';
import {
  businessHoursToPortalFormat,
  normalizeSlug,
  normalizeStoreTheme,
  parseBusinessHours,
  storeProfileToDraft,
  type StoreProfile,
  type StoreProfileDraft,
} from '../domain/storeProfileTypes';

type RemoteStoreProfileRow = {
  branch_id: string;
  slug: string;
  display_name: string | null;
  logo_url: string | null;
  cover_storage_path: string | null;
  background_storage_path: string | null;
  store_theme?: string | null;
  description: string | null;
  phone: string | null;
  whatsapp: string | null;
  order_contact_message?: boolean | null;
  order_contact_whatsapp?: boolean | null;
  order_contact_phone?: boolean | null;
  is_public: boolean;
  delivery_enabled: boolean;
  own_delivery_enabled: boolean;
  vuelto_riders_enabled: boolean;
  is_on_duty: boolean;
  business_hours: unknown;
  manual_override: string | null;
  manual_override_until: string | null;
  tc_accepted_at: string | null;
  max_concurrent_orders: number | null;
  default_prep_minutes: number | null;
  auto_accept_pickup_paid: boolean | null;
  min_order_amount?: number | null;
  cover_focal_x?: number | null;
  cover_focal_y?: number | null;
  cover_zoom?: number | null;
  logo_focal_x?: number | null;
  logo_focal_y?: number | null;
  logo_zoom?: number | null;
  menu_carta_enabled?: boolean | null;
  notify_customer_ready_pickup?: boolean | null;
};

function mapRemote(row: RemoteStoreProfileRow): StoreProfile {
  const override = row.manual_override;
  return {
    branchId: row.branch_id,
    slug: row.slug,
    displayName: row.display_name ?? null,
    logoUrl: row.logo_url,
    coverStoragePath: row.cover_storage_path,
    backgroundStoragePath: row.background_storage_path,
    storeTheme: normalizeStoreTheme(row.store_theme),
    description: row.description,
    phone: row.phone,
    whatsapp: row.whatsapp,
    orderContactMessage: row.order_contact_message !== false,
    orderContactWhatsapp: row.order_contact_whatsapp === true,
    orderContactPhone: row.order_contact_phone === true,
    isPublic: row.is_public === true,
    deliveryEnabled: row.delivery_enabled === true,
    ownDeliveryEnabled: row.own_delivery_enabled === true,
    vueltoRidersEnabled: row.vuelto_riders_enabled === true,
    isOnDuty: row.is_on_duty === true,
    businessHours: parseBusinessHours(row.business_hours),
    manualOverride: override === 'force_open' || override === 'force_close' ? override : 'auto',
    manualOverrideUntil: row.manual_override_until,
    tcAcceptedAt: row.tc_accepted_at,
    maxConcurrentOrders:
      row.max_concurrent_orders != null && Number(row.max_concurrent_orders) > 0
        ? Number(row.max_concurrent_orders)
        : null,
    defaultPrepMinutes:
      row.default_prep_minutes != null && Number(row.default_prep_minutes) > 0
        ? Number(row.default_prep_minutes)
        : null,
    autoAcceptPickupPaid: row.auto_accept_pickup_paid === true,
    minOrderAmount:
      row.min_order_amount != null && Number(row.min_order_amount) > 0
        ? Number(row.min_order_amount)
        : null,
    coverFocalX: row.cover_focal_x != null ? Number(row.cover_focal_x) : 50,
    coverFocalY: row.cover_focal_y != null ? Number(row.cover_focal_y) : 50,
    coverZoom: row.cover_zoom != null ? Number(row.cover_zoom) : 1,
    logoFocalX: row.logo_focal_x != null ? Number(row.logo_focal_x) : 50,
    logoFocalY: row.logo_focal_y != null ? Number(row.logo_focal_y) : 50,
    logoZoom: row.logo_zoom != null ? Number(row.logo_zoom) : 1,
    menuCartaEnabled: row.menu_carta_enabled !== false,
    notifyCustomerReadyPickup: row.notify_customer_ready_pickup === true,
  };
}

function decodeBase64(b64: string, mimeType: string): Uint8Array | Blob {
  if (Platform.OS === 'web') {
    const binary = atob(b64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new Blob([bytes], { type: mimeType });
  }
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function extensionForMime(mime: string): string {
  if (mime.includes('png')) return 'png';
  if (mime.includes('webp')) return 'webp';
  return 'jpg';
}

function storeProfileRpcError(error: { message?: string } | null): Error {
  const raw = (error?.message ?? '').toLowerCase();
  if (raw.includes('slug_taken')) {
    return new Error('Ese slug ya está en uso. Elegí otro.');
  }
  if (raw.includes('slug_invalid') || raw.includes('slug_required')) {
    return new Error('Slug inválido. Usá solo minúsculas, números y guiones.');
  }
  if (
    raw.includes('failed to fetch') ||
    raw.includes('network') ||
    raw.includes('offline') ||
    raw.includes('fetch')
  ) {
    return new Error('Necesitás conexión para guardar el perfil.');
  }
  return new Error(error?.message || 'No se pudo guardar el perfil público.');
}

export async function getStoreProfile(branchId: string): Promise<StoreProfile | null> {
  const id = branchId.trim();
  if (!id) return null;
  const { data, error } = await supabase
    .from('public_store_profiles')
    .select('branch_id, slug, display_name, logo_url, cover_storage_path, background_storage_path, store_theme, description, phone, whatsapp, order_contact_message, order_contact_whatsapp, order_contact_phone, is_public, delivery_enabled, own_delivery_enabled, vuelto_riders_enabled, is_on_duty, business_hours, manual_override, manual_override_until, tc_accepted_at, max_concurrent_orders, default_prep_minutes, auto_accept_pickup_paid, notify_customer_ready_pickup, min_order_amount, cover_focal_x, cover_focal_y, cover_zoom, logo_focal_x, logo_focal_y, logo_zoom, menu_carta_enabled')
    .eq('branch_id', id)
    .maybeSingle();
  if (error) {
    throw new Error(error.message || 'No se pudo cargar el perfil público.');
  }
  if (!data) return null;
  return mapRemote(data as RemoteStoreProfileRow);
}

export async function isSlugAvailable(slug: string, excludeBranchId?: string | null): Promise<boolean> {
  const normalized = normalizeSlug(slug);
  if (!normalized) return false;
  let query = supabase
    .from('public_store_profiles')
    .select('branch_id')
    .eq('slug', normalized)
    .limit(1);
  if (excludeBranchId) {
    query = query.neq('branch_id', excludeBranchId);
  }
  const { data, error } = await query.maybeSingle();
  if (error) {
    throw new Error('Necesitás conexión para verificar el slug.');
  }
  return data == null;
}

/** Escritura online (sin cola de sync): validaciones de slug viven en el servidor. */
export async function updateStoreProfile(branchId: string, draft: StoreProfileDraft): Promise<void> {
  const id = branchId.trim();
  if (!id) throw new Error('Sucursal requerida.');
  const slug = normalizeSlug(draft.slug);
  if (!slug) throw new Error('Ingresá un slug para la tienda pública.');

  const updatedAt = new Date().toISOString();
  const { error } = await supabase.rpc('apply_store_profile_command', {
    p_operation: 'update',
    p_payload: {
      branchId: id,
      operationId: `${id}:${updatedAt}`,
      slug,
      displayName: draft.displayName.trim() || null,
      logoUrl: draft.logoUrl.trim() || null,
      coverStoragePath: draft.coverStoragePath.trim() || null,
      backgroundStoragePath: draft.backgroundStoragePath.trim() || null,
      storeTheme: normalizeStoreTheme(draft.storeTheme),
      description: draft.description.trim() || null,
      phone: draft.phone.trim() || null,
      whatsapp: draft.whatsapp.trim() || null,
      orderContactMessage: draft.orderContactMessage === true,
      orderContactWhatsapp: draft.orderContactWhatsapp === true,
      orderContactPhone: draft.orderContactPhone === true,
      isPublic: draft.isPublic === true,
      isOnDuty: draft.isOnDuty === true,
      businessHours: businessHoursToPortalFormat(draft.businessHours),
      manualOverride: draft.manualOverride,
      manualOverrideUntil: draft.manualOverrideUntil,
      tcAcceptedAt: draft.tcAcceptedAt,
      maxConcurrentOrders: draft.maxConcurrentOrders,
      defaultPrepMinutes: draft.defaultPrepMinutes,
      autoAcceptPickupPaid: draft.autoAcceptPickupPaid === true,
      notifyCustomerReadyPickup: draft.notifyCustomerReadyPickup === true,
      minOrderAmount: draft.minOrderAmount,
      coverFocalX: draft.coverFocalX,
      coverFocalY: draft.coverFocalY,
      coverZoom: draft.coverZoom,
      logoFocalX: draft.logoFocalX,
      logoFocalY: draft.logoFocalY,
      logoZoom: draft.logoZoom,
      menuCartaEnabled: draft.menuCartaEnabled === true,
      updatedAt,
    },
  });
  if (error) throw storeProfileRpcError(error);
  invalidateStorefrontStatus(id);
}

/** Pausa / reopen rápido sin pasar por el formulario completo de tienda. */
export async function setStoreManualOverride(
  branchId: string,
  mode: StoreProfileDraft['manualOverride'],
  durationMinutes?: number,
): Promise<StoreProfile> {
  const profile = await getStoreProfile(branchId);
  if (!profile) throw new Error('Publicá o configurá el perfil de tienda primero.');
  const until =
    mode !== 'auto' && durationMinutes
      ? new Date(Date.now() + durationMinutes * 60_000).toISOString()
      : null;
  const draft = storeProfileToDraft(profile);
  draft.manualOverride = mode;
  draft.manualOverrideUntil = until;
  await updateStoreProfile(branchId, draft);
  const next = { ...profile, manualOverride: mode, manualOverrideUntil: until };
  storefrontProfileCache.set(branchId, next);
  return next;
}

export async function uploadStoreLogo(params: {
  orgId: string;
  branchId: string;
  base64: string;
  mimeType: string;
}): Promise<string> {
  const { orgId, branchId, base64, mimeType } = params;
  const ext = extensionForMime(mimeType);
  const storagePath = `${orgId}/${branchId}.${ext}`;

  const allExts = ['jpg', 'png', 'webp'].filter((e) => e !== ext);
  for (const oldExt of allExts) {
    try {
      await supabase.storage.from('store-logos').remove([`${orgId}/${branchId}.${oldExt}`]);
    } catch {
      // ignore — file may not exist
    }
  }

  const { error: uploadError } = await supabase.storage
    .from('store-logos')
    .upload(storagePath, decodeBase64(base64, mimeType), {
      contentType: mimeType,
      upsert: true,
    });

  if (uploadError) {
    throw new Error(`No se pudo subir el logo: ${uploadError.message}`);
  }

  const { data } = supabase.storage.from('store-logos').getPublicUrl(storagePath);
  return `${data.publicUrl}?t=${Date.now()}`;
}

export async function removeStoreLogo(params: {
  orgId: string;
  branchId: string;
}): Promise<void> {
  const { orgId, branchId } = params;
  for (const ext of ['jpg', 'png', 'webp']) {
    try {
      await supabase.storage.from('store-logos').remove([`${orgId}/${branchId}.${ext}`]);
    } catch {
      // ignore — file may not exist
    }
  }
}

export async function uploadStoreCover(params: {
  orgId: string;
  branchId: string;
  base64: string;
  mimeType: string;
}): Promise<string> {
  const { orgId, branchId, base64, mimeType } = params;
  const ext = extensionForMime(mimeType);
  const storagePath = `${orgId}/${branchId}-cover.${ext}`;

  const allExts = ['jpg', 'png', 'webp'].filter((e) => e !== ext);
  for (const oldExt of allExts) {
    try {
      await supabase.storage.from('store-covers').remove([`${orgId}/${branchId}-cover.${oldExt}`]);
    } catch {
      // ignore — file may not exist
    }
  }

  const { error: uploadError } = await supabase.storage
    .from('store-covers')
    .upload(storagePath, decodeBase64(base64, mimeType), {
      contentType: mimeType,
      upsert: true,
    });

  if (uploadError) {
    throw new Error(`No se pudo subir la portada: ${uploadError.message}`);
  }

  const { data } = supabase.storage.from('store-covers').getPublicUrl(storagePath);
  return `${data.publicUrl}?t=${Date.now()}`;
}

export async function removeStoreCover(params: {
  orgId: string;
  branchId: string;
}): Promise<void> {
  const { orgId, branchId } = params;
  for (const ext of ['jpg', 'png', 'webp']) {
    try {
      await supabase.storage.from('store-covers').remove([`${orgId}/${branchId}-cover.${ext}`]);
    } catch {
      // ignore — file may not exist
    }
  }
}

/** Fondo de tienda (mismo bucket store-covers, sufijo -bg). Sube y devuelve URL publica. */
export async function uploadStoreBackground(params: {
  orgId: string;
  branchId: string;
  base64: string;
  mimeType: string;
}): Promise<string> {
  const { orgId, branchId, base64, mimeType } = params;
  const ext = extensionForMime(mimeType);
  const storagePath = `${orgId}/${branchId}-bg.${ext}`;

  for (const oldExt of ['jpg', 'png', 'webp'].filter((e) => e !== ext)) {
    try {
      await supabase.storage.from('store-covers').remove([`${orgId}/${branchId}-bg.${oldExt}`]);
    } catch {
      // ignore
    }
  }

  const { error: uploadError } = await supabase.storage
    .from('store-covers')
    .upload(storagePath, decodeBase64(base64, mimeType), {
      contentType: mimeType,
      upsert: true,
    });
  if (uploadError) throw new Error(`No se pudo subir el fondo: ${uploadError.message}`);

  const { data } = supabase.storage.from('store-covers').getPublicUrl(storagePath);
  return `${data.publicUrl}?t=${Date.now()}`;
}

export async function removeStoreBackground(params: {
  orgId: string;
  branchId: string;
}): Promise<void> {
  const { orgId, branchId } = params;
  for (const ext of ['jpg', 'png', 'webp']) {
    try {
      await supabase.storage.from('store-covers').remove([`${orgId}/${branchId}-bg.${ext}`]);
    } catch {
      // ignore
    }
  }
}
